<template>
  <div>
    <v-alert dense outlined type="info" class="mb-4 text-caption">
      Horario de reservación (referencia): 10:40 a 20:40, bloques de 30 min. Límite para maestros: 2 h/día por escuela (4 bloques), sin traslapes entre escuelas ni dos servidores a la misma hora — la validación en tiempo real irá en el panel de reservación.
      Los días inhábiles o festivos se toman del módulo de Días festivos (no se configuran aquí).
    </v-alert>

    <v-expansion-panels v-model="panel" multiple>
      <!-- Servidores (salones virtuales) -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-subtitle-2">
          Servidores (salones virtuales) — Zoom y disponibilidad
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row justify="end" align="center" class="mb-2">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="searchSalones"
                label="Buscar"
                filled
                dense
                single-line
                rounded
                hide-details
                append-icon="mdi-magnify"
              />
            </v-col>
            <v-col cols="12" md="auto" class="py-0">
              <v-switch
                v-model="soloConServidor"
                label="Con servidor"
                hide-details
                dense
                class="mt-0"
              />
            </v-col>
            <v-col cols="auto" class="d-flex align-center flex-wrap">
              <v-btn rounded class="mr-2" small dark color="green" @click="exportExcel(salonesFiltrados, 'Salones_virtuales_' + etiquetaEscuela)">
                <v-icon small>mdi-microsoft-excel</v-icon>
              </v-btn>
              <v-btn color="primary" rounded dark small @click="cargarSalones">
                <v-icon small left>mdi-refresh</v-icon>
                Actualizar
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headersSalones"
            :items="salonesFiltrados"
            class="elevation-0"
            :search="searchSalones"
            dense
            :mobile-breakpoint="100"
          >
            <template v-slot:item.actions="{ item }">
              <v-btn text icon color="primary" x-small @click="editarSalon(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <v-btn text color="primary" small @click="cargarSalones">Actualizar</v-btn>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bloqueos por horario de clases -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-subtitle-2">
          Bloqueo automático por uso en clases (servidor no disponible en ese horario)
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row justify="end" class="mb-2">
            <v-btn color="orange" dark small tile class="mr-2" @click="abrirDialogBloqueo()">
              <v-icon small left>mdi-plus</v-icon>
              Nuevo bloqueo
            </v-btn>
            <v-btn color="primary" dark small tile @click="cargarBloqueos">
              <v-icon small left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-row>
          <v-data-table
            :headers="headersBloqueos"
            :items="bloqueosClase"
            class="elevation-0"
            dense
            :loading="cargandoBloqueos"
          >
            <template v-slot:item.dias="{ item }">
              <span class="text-caption">{{ formatearDiasSemana(item) }}</span>
            </template>
            <template v-slot:item.horario="{ item }">
              {{ item.hora_inicio }} – {{ item.hora_final }}
            </template>
            <template v-slot:item.activo_sn="{ item }">
              <v-chip v-if="item.activo_sn == 1" small color="green" dark>Sí</v-chip>
              <v-chip v-else small color="error" dark>No</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn color="primary" x-small class="mr-2" @click="abrirDialogBloqueo(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn color="error" x-small @click="eliminarBloqueo(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <span class="text-caption grey--text">Sin bloqueos configurados o servicio no disponible.</span>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Consulta de reservaciones por rango -->
      <v-expansion-panel>
        <v-expansion-panel-header class="text-subtitle-2">
          Reservaciones por rango (bloques fijos, una columna por servidor)
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p class="text-caption grey--text mb-3">
            Los <strong>bloques de hora están predefinidos</strong> (30 min, de 10:40 a 20:40). Cada <strong>columna es un servidor</strong>
            (el texto del campo Servidor del salón). En cada celda se muestra el maestro que reservó en ese bloque.
          </p>
          <v-row align="end" class="mb-2">
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="fechaReservaDesde"
                type="date"
                label="Desde"
                filled
                dense
                hide-details
                rounded
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="fechaReservaHasta"
                type="date"
                label="Hasta"
                filled
                dense
                hide-details
                rounded
              />
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" :loading="cargandoReservasRango" rounded small @click="consultarReservasRango">
                <v-icon small left>mdi-magnify</v-icon>
                Consultar
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn
                small
                dark
                color="green"
                tile
                :disabled="!filasReservasPlanas.length"
                @click="exportExcel(filasReservasPlanas, 'Reservas_salones_' + etiquetaEscuela)"
              >
                <v-icon small>mdi-microsoft-excel</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <p v-if="!salonesConServidorList.length" class="text-caption amber--text text--darken-2 mb-2">
            No hay salones con el campo «Servidor» cargado para esta escuela. Configúralo en la sección superior.
          </p>
          <div v-else-if="consultaReservasEjecutada && !cargandoReservasRango">
            <template v-if="diasEnRangoConsulta.length">
              <v-tabs v-model="tabDiaReserva" show-arrows class="mb-2">
                <v-tab v-for="d in diasEnRangoConsulta" :key="'tab-' + d">{{ formatearFechaCorta(d) }}</v-tab>
              </v-tabs>
              <v-tabs-items v-model="tabDiaReserva">
                <v-tab-item v-for="d in diasEnRangoConsulta" :key="'item-' + d">
                  <div class="overflow-x-auto">
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-left bloque-col">Bloque</th>
                          <th
                            v-for="s in salonesConServidorList"
                            :key="'h-' + d + '-' + s.id_salon"
                            class="text-center text-caption salon-col"
                          >
                            {{ s.servidor }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="blk in bloquesPredefinidos" :key="'r-' + d + '-' + blk.label">
                          <td class="text-caption">{{ blk.label }}</td>
                          <td
                            v-for="s in salonesConServidorList"
                            :key="'c-' + d + '-' + s.id_salon + '-' + blk.inicio"
                            class="text-caption text-center"
                          >
                            <v-chip
                              v-if="nombreMaestroEnCelda(d, s.id_salon, blk)"
                              x-small
                              dense
                              label
                              color="light-green lighten-4"
                              class="black--text teacher-reserva-chip teacher-chip-click"
                              @click.stop="abrirDialogLinkZoom(d, s.id_salon, blk)"
                            >
                              {{ nombreMaestroEnCelda(d, s.id_salon, blk) }}
                            </v-chip>
                            <span v-else class="grey--text">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </template>
          </div>
          <v-progress-linear v-if="cargandoReservasRango" indeterminate color="primary" class="mt-2" />
          <p v-if="consultaReservasEjecutada && !cargandoReservasRango && !filasReservasPlanas.length && salonesConServidorList.length" class="text-caption grey--text mt-2">
            No hay reservaciones en el rango seleccionado (o el servicio aún no devuelve datos).
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Dialog link Zoom (clic en maestro en grilla de reservas) -->
    <v-dialog v-model="dialogLinkZoom" max-width="420px" @click:outside="dialogLinkZoom = false">
      <v-card>
        <v-card-title class="text-subtitle-2 py-2 pr-2">
          Link Zoom
          <v-spacer />
          <v-btn icon small aria-label="Cerrar" @click="dialogLinkZoom = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pb-3 pt-0">
          <div class="text-caption grey--text">{{ linkZoomDialogo.maestro }}</div>
          <div v-if="linkZoomDialogo.salon" class="text-caption">{{ linkZoomDialogo.salon }}</div>
          <div class="text-caption mb-2">{{ linkZoomDialogo.fecha }} · {{ linkZoomDialogo.bloque }}</div>
          <v-textarea
            v-if="linkZoomDialogo.link_zoom"
            :value="linkZoomDialogo.link_zoom"
            readonly
            outlined
            dense
            auto-grow
            rows="2"
            hide-details
            class="text-caption"
          />
          <p v-else class="text-caption grey--text mb-0">No hay link Zoom configurado para este salón.</p>
          <v-btn
            v-if="linkZoomDialogo.link_zoom"
            small
            color="primary"
            class="mt-2"
            :href="linkZoomDialogo.link_zoom"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon small left>mdi-open-in-new</v-icon>
            Abrir en navegador
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog salón -->
    <v-dialog v-model="dialogSalon" max-width="560px" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          Editar servidor
          <v-spacer />
          <span v-if="editedItemSalon.id_salon" class="text-caption">ID: {{ editedItemSalon.id_salon }}</span>
        </v-card-title>
        <v-card-text>
          <v-text-field :value="editedItemSalon.salon" label="Nombre del salón" filled dense readonly />
          <v-text-field
            v-model="editedItemSalon.servidor"
            label="Servidor"
            filled
            dense
            hint="Texto libre (identificador o nota del servidor)"
            persistent-hint
          />
          <v-textarea
            :value="editedItemSalon.link_zoom || ''"
            label="Link Zoom"
            filled
            dense
            readonly
            auto-grow
            rows="2"
            hide-details
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color="black" dark small tile @click="cerrarDialogSalon">
            <v-icon small left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn color="primary" small dark tile @click="guardarSalon">
            <v-icon small left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog bloqueo clase -->
    <v-dialog v-model="dialogBloqueo" max-width="560px" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ editedItemBloqueo.id_bloqueo_clase ? 'Editar bloqueo' : 'Nuevo bloqueo' }}
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="editedItemBloqueo.id_salon"
            :items="salonesFiltrados"
            item-text="salon"
            item-value="id_salon"
            label="Salón *"
            filled
            dense
          />
          <div class="text-caption mb-1">Días de la semana</div>
          <v-row dense class="mb-2">
            <v-col v-for="d in diasSemanaOpts" :key="d.bit" cols="6" sm="4" md="3" class="py-0">
              <v-checkbox
                :input-value="tieneDia(editedItemBloqueo, d.bit)"
                :label="d.label"
                hide-details
                dense
                @change="(v) => toggleDia(editedItemBloqueo, d.bit, v)"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field v-model="editedItemBloqueo.hora_inicio" label="Hora inicio *" type="time" filled dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editedItemBloqueo.hora_final" label="Hora fin *" type="time" filled dense hide-details />
            </v-col>
          </v-row>
          <v-switch v-model="editedItemBloqueo.activo_sn" :true-value="1" :false-value="0" label="Regla activa" hide-details />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialogBloqueo = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="primary" dark @click="guardarBloqueo">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'
import funcionesExcel from '@/mixins/funcionesExcel'

const defaultSalon = () => ({
  id_salon: 0,
  salon: '',
  servidor: '',
  link_zoom: '',
})

const defaultBloqueo = (escuela) => ({
  id_bloqueo_clase: 0,
  id_salon: null,
  escuela,
  // Por regla de negocio, fin de semana queda bloqueado por defecto (Sáb + Dom).
  dias_bitmask: 32 + 64,
  hora_inicio: '18:00',
  hora_final: '19:30',
  activo_sn: 1,
  deleted: 0,
})

function pad2(n) {
  return n < 10 ? '0' + n : String(n)
}

/** Bloques de reservación predefinidos: 10:40–11:10 … 20:10–20:40 (30 min c/u). No se calculan en runtime salvo esta lista fija. */
function generarBloques30Min() {
  const blocks = []
  let minutes = 10 * 60 + 40
  const maxStart = 20 * 60 + 10
  while (minutes <= maxStart) {
    const h1 = Math.floor(minutes / 60)
    const m1 = minutes % 60
    const minutesEnd = minutes + 30
    const h2 = Math.floor(minutesEnd / 60)
    const m2 = minutesEnd % 60
    const inicio = pad2(h1) + ':' + pad2(m1)
    const fin = pad2(h2) + ':' + pad2(m2)
    blocks.push({ inicio, fin, label: inicio + ' – ' + fin })
    minutes += 30
  }
  return blocks
}

function fechaHoyISO() {
  const d = new Date()
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate())
}

function fechaMasDiasISO(dias) {
  const d = new Date()
  d.setDate(d.getDate() + dias)
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate())
}

export default {
  name: 'AdminEscuelaReservacion',

  components: { Alerta, carga },

  mixins: [validarErrores, funcionesExcel],

  props: {
    escuela: {
      type: Number,
      required: true,
      validator: (v) => v === 1 || v === 2,
    },
  },

  data: () => ({
    parametros: {
      dialogError: false,
      mensaje: '',
      color: '',
    },
    respuestaAlerta: false,
    cargar: false,

    panel: [0, 1, 2],
    searchSalones: '',
    soloConServidor: false,
    salones: [],

    bloqueosClase: [],
    cargandoBloqueos: false,

    fechaReservaDesde: fechaHoyISO(),
    fechaReservaHasta: fechaMasDiasISO(7),
    reservasRango: [],
    cargandoReservasRango: false,
    consultaReservasEjecutada: false,
    rangoReservasConsulta: { desde: null, hasta: null },
    tabDiaReserva: 0,
    bloquesPredefinidos: generarBloques30Min(),

    dialogLinkZoom: false,
    linkZoomDialogo: {
      maestro: '',
      link_zoom: '',
      salon: '',
      bloque: '',
      fecha: '',
    },

    dialogSalon: false,
    editedIndexSalon: -1,
    editedItemSalon: defaultSalon(),

    dialogBloqueo: false,
    editedItemBloqueo: defaultBloqueo(1),
    editedIndexBloqueo: -1,

    headersSalones: [
      { text: 'ID', value: 'id_salon' },
      { text: 'Nombre', value: 'salon' },
      { text: 'Servidor', value: 'servidor' },
      { text: 'Acciones', value: 'actions', sortable: false },
    ],
    headersBloqueos: [
      { text: 'Salón', value: 'nombre_salon' },
      { text: 'Días', value: 'dias' },
      { text: 'Horario', value: 'horario' },
      { text: 'Activo', value: 'activo_sn' },
      { text: 'Acciones', value: 'actions', sortable: false },
    ],

    diasSemanaOpts: [
      { label: 'Lun', bit: 1 },
      { label: 'Mar', bit: 2 },
      { label: 'Mié', bit: 4 },
      { label: 'Jue', bit: 8 },
      { label: 'Vie', bit: 16 },
      { label: 'Sáb', bit: 32 },
      { label: 'Dom', bit: 64 },
    ],
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    etiquetaEscuela() {
      return this.escuela === 1 ? 'INBI' : 'FAST'
    },

    salonesFiltrados() {
      let id_platel = this.escuela == 1 ? 17 : 16;
      let list = this.salones.filter((s) => s.id_plantel == id_platel)
      if (this.soloConServidor) {
        list = list.filter((s) => String(s.servidor || '').trim() !== '')
      }
      return list
    },

    /** Salones de la escuela con campo servidor (texto) para la grilla de reservas */
    salonesConServidorList() {
      const idPlantel = this.escuela == 1 ? 17 : 16
      return this.salones.filter(
        (s) => s.id_plantel == idPlantel && String(s.servidor || '').trim() !== ''
      )
    },

    diasEnRangoConsulta() {
      if (!this.rangoReservasConsulta.desde || !this.rangoReservasConsulta.hasta) return []
      return this.enumerarDias(this.rangoReservasConsulta.desde, this.rangoReservasConsulta.hasta)
    },

    reservasRangoFiltradas() {
      const ids = {}
      for (let i = 0; i < this.salonesConServidorList.length; i++) {
        const id = this.salonesConServidorList[i].id_salon
        ids[id] = true
        ids[String(id)] = true
      }
      return this.reservasRango.filter((r) => ids[r.id_salon] || ids[String(r.id_salon)])
    },

    filasReservasPlanas() {
      const rows = []
      const list = this.reservasRangoFiltradas
      for (let i = 0; i < list.length; i++) {
        const r = list[i]
        rows.push({
          fecha: r.fecha,
          bloque: (r.hora_inicio || '') + ' – ' + (r.hora_final || ''),
          salon: r.salon || '',
          servidor: r.servidor || '',
          maestro: r.maestro || r.nombre_maestro || r.teacher || r.nombre_teacher || '',
        })
      }
      return rows
    },
  },

  watch: {
    escuela: {
      immediate: true,
      async handler() {
        await this.cargarSalones()
        await this.cargarBloqueos()
      },
    },
  },

  methods: {
    enumerarDias(desdeStr, hastaStr) {
      const out = []
      const a = desdeStr.split('-')
      const b = hastaStr.split('-')
      const start = new Date(parseInt(a[0], 10), parseInt(a[1], 10) - 1, parseInt(a[2], 10))
      const end = new Date(parseInt(b[0], 10), parseInt(b[1], 10) - 1, parseInt(b[2], 10))
      const cur = new Date(start.getTime())
      while (cur <= end) {
        out.push(
          cur.getFullYear() + '-' + pad2(cur.getMonth() + 1) + '-' + pad2(cur.getDate())
        )
        cur.setDate(cur.getDate() + 1)
      }
      return out
    },

    formatearFechaCorta(f) {
      const p = f.split('-')
      if (p.length !== 3) return f
      return pad2(parseInt(p[2], 10)) + '/' + pad2(parseInt(p[1], 10)) + '/' + p[0]
    },

    horaAMinutos(t) {
      if (!t || typeof t !== 'string') return 0
      const p = t.split(':')
      const h = parseInt(p[0], 10) || 0
      const m = parseInt(p[1], 10) || 0
      return h * 60 + m
    },

    /** Nombre del maestro en el bloque, o null si no hay reserva / sin nombre (para chip). */
    nombreMaestroEnCelda(fecha, idSalon, blk) {
      const r = this.reservaEnCelda(fecha, idSalon, blk)
      if (!r) return null
      const nombre = (r.maestro || r.nombre_maestro || r.teacher || r.nombre_teacher || '').trim()
      return nombre || null
    },

    /** Registro de reserva que coincide con la celda, o null. */
    reservaEnCelda(fecha, idSalon, blk) {
      const bs = this.horaAMinutos(blk.inicio)
      const be = this.horaAMinutos(blk.fin)
      const list = this.reservasRangoFiltradas
      for (let i = 0; i < list.length; i++) {
        const r = list[i]
        if (r.fecha !== fecha || (r.id_salon != idSalon && String(r.id_salon) !== String(idSalon))) continue
        const rs = this.horaAMinutos(r.hora_inicio)
        const re = this.horaAMinutos(r.hora_final)
        if (rs < be && re > bs) {
          return r
        }
      }
      return null
    },

    abrirDialogLinkZoom(fecha, idSalon, blk) {
      const r = this.reservaEnCelda(fecha, idSalon, blk)
      const sal = this.salones.find(function (s) {
        return s.id_salon == idSalon || String(s.id_salon) === String(idSalon)
      })
      const link =
        (r && r.link_zoom) ||
        (sal && sal.link_zoom) ||
        ''
      this.linkZoomDialogo = {
        maestro: this.nombreMaestroEnCelda(fecha, idSalon, blk) || '',
        link_zoom: typeof link === 'string' ? link.trim() : '',
        salon: (sal && sal.salon) || (r && r.salon) || '',
        bloque: blk.label,
        fecha: fecha,
      }
      this.dialogLinkZoom = true
    },

    consultarReservasRango() {
      if (!this.fechaReservaDesde || !this.fechaReservaHasta) {
        this.validarErrorDirecto('Indica fecha desde y hasta.')
        return
      }
      if (this.fechaReservaDesde > this.fechaReservaHasta) {
        this.validarErrorDirecto('La fecha inicial no puede ser mayor que la final.')
        return
      }
      if (!this.salonesConServidorList.length) {
        this.validarErrorDirecto('No hay salones con «Servidor» configurado para listar.')
        return
      }
      this.cargandoReservasRango = true
      this.consultaReservasEjecutada = true
      this.$http
        .get(
          'teachersmode.get.reservas_salones_rango/' +
            this.escuela +
            '/' +
            this.fechaReservaDesde +
            '/' +
            this.fechaReservaHasta
        )
        .then((response) => {
          const raw = response.data.reservas || response.data.items || response.data
          this.reservasRango = Array.isArray(raw) ? raw : []
          this.rangoReservasConsulta = {
            desde: this.fechaReservaDesde,
            hasta: this.fechaReservaHasta,
          }
          this.tabDiaReserva = 0
        })
        .catch(() => {
          this.reservasRango = []
          this.rangoReservasConsulta = {
            desde: this.fechaReservaDesde,
            hasta: this.fechaReservaHasta,
          }
        })
        .finally(() => {
          this.cargandoReservasRango = false
        })
    },

    editarSalon(item) {
      this.editedIndexSalon = this.salonesFiltrados.indexOf(item)
      this.editedItemSalon = {
        ...item,
        servidor: item.servidor != null ? String(item.servidor) : '',
      }
      this.dialogSalon = true
    },

    cerrarDialogSalon() {
      this.dialogSalon = false
      this.$nextTick(() => {
        this.editedItemSalon = defaultSalon()
        this.editedIndexSalon = -1
      })
    },

    guardarSalon() {
      const e = { ...this.editedItemSalon }
      if (!e.id_salon) {
        this.validarErrorDirecto('Salón no válido.')
        return
      }
      e.id_usuario = this.getdatosUsuarioERPERP.iderp
      this.cargar = true
      this.$http
        .put('salones.update/' + e.id_salon, e)
        .then((response) => {
          this.validarSuccess(response.data.message || 'Guardado')
          this.cargarSalones()
          this.cerrarDialogSalon()
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : error)
        })
        .finally(() => {
          this.cargar = false
        })
    },

    cargarSalones() {
      this.cargar = true
      this.salones = []
      return this.$http
        .get('salones.list')
        .then((response) => {
          this.salones = response.data || []
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : error)
        })
        .finally(() => {
          this.cargar = false
        })
    },

    cargarBloqueos() {
      this.cargandoBloqueos = true
      this.bloqueosClase = []
      return this.$http
        .get(`teachersmode.get.bloqueos_clase/${this.escuela}`, {
          params: { _t: Date.now() },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        })
        .then((response) => {
          const body = response.data || {}
          const raw = Array.isArray(body)
            ? body
            : Array.isArray(body.items)
              ? body.items
              : Array.isArray(body.bloqueos)
                ? body.bloqueos
                : []
          this.bloqueosClase = raw.map((b) => this.normalizarBloqueoFila(b))
        })
        .catch(() => {
          this.bloqueosClase = []
        })
        .finally(() => {
          this.cargandoBloqueos = false
        })
    },

    normalizarBloqueoFila(b) {
      const idRef = b.id_salon != null ? b.id_salon : b.idsalones_virtuales
      const sal = this.salones.find(
        (s) => s.id_salon == idRef || s.idsalones_virtuales == b.idsalones_virtuales
      )
      const nombre = b.nombre_salon || (sal && sal.salon) || '—'
      const diasBitmask = b.dias_bitmask == null ? 96 : Number(b.dias_bitmask)
      return { ...b, dias_bitmask: diasBitmask, nombre_salon: nombre }
    },

    tieneDia(item, bit) {
      const mask = Number(item.dias_bitmask != null ? item.dias_bitmask : 0)
      return (mask & bit) !== 0
    },

    toggleDia(item, bit, active) {
      let mask = Number(item.dias_bitmask != null ? item.dias_bitmask : 0)
      if (active) {
        mask |= bit
      } else {
        mask &= ~bit
      }
      this.$set(item, 'dias_bitmask', mask)
    },

    formatearDiasSemana(item) {
      const mask = Number(item.dias_bitmask != null ? item.dias_bitmask : 0)
      if (!mask) return '—'
      const parts = []
      this.diasSemanaOpts.forEach((d) => {
        if ((mask & d.bit) !== 0) parts.push(d.label)
      })
      return parts.join(', ')
    },

    abrirDialogBloqueo(item) {
      if (item && item.id_bloqueo_clase) {
        this.editedIndexBloqueo = this.bloqueosClase.indexOf(item)
        const merged = Object.assign(defaultBloqueo(this.escuela), item)
        merged.id_salon = item.id_salon != null ? item.id_salon : item.idsalones_virtuales
        this.editedItemBloqueo = merged
      } else {
        this.editedIndexBloqueo = -1
        this.editedItemBloqueo = defaultBloqueo(this.escuela)
      }
      this.dialogBloqueo = true
    },

    guardarBloqueo() {
      const b = { ...this.editedItemBloqueo, idsalones_virtuales: this.editedItemBloqueo.id_salon }
      if (!b.id_salon || !b.hora_inicio || !b.hora_final) {
        this.validarErrorDirecto('Salón y horario son obligatorios.')
        return
      }
      if (b.hora_inicio >= b.hora_final) {
        this.validarErrorDirecto('La hora de inicio debe ser menor que la hora final.')
        return
      }
      const teacherId = Number(this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp)
      if (!Number.isFinite(teacherId) || teacherId <= 0) {
        this.validarErrorDirecto('No se pudo identificar al docente (teacherId).')
        return
      }
      b.escuela = this.escuela
      b.teacherId = teacherId
      b.escuelaId = Number(this.escuela)
      b.fecha = fechaHoyISO()
      b.horaInicio = b.hora_inicio
      b.horaFin = b.hora_final
      this.cargar = true
      const req =
        this.editedIndexBloqueo > -1 && b.id_bloqueo_clase
          ? this.$http.put('teachersmode.put.bloqueos_clase/' + b.id_bloqueo_clase, b)
          : this.$http.post('teachersmode.post.bloqueos_clase', b)
      req
        .then(() => {
          this.dialogBloqueo = false
          this.cargarBloqueos()
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : error)
        })
        .finally(() => {
          this.cargar = false
        })
    },

    eliminarBloqueo(item) {
      this.cargar = true
      this.$http
        .put('teachersmode.put.bloqueos_clase/' + item.id_bloqueo_clase, { ...item, deleted: 1 })
        .then(() => this.cargarBloqueos())
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : error)
        })
        .finally(() => {
          this.cargar = false
        })
    },
  },
}
</script>

<style scoped>
.bloque-col {
  min-width: 130px;
}
.salon-col {
  min-width: 100px;
}
.teacher-reserva-chip.v-chip {
  height: auto !important;
  min-height: 22px;
  font-size: 0.7rem;
  padding: 0 6px;
}
.teacher-chip-click {
  cursor: pointer;
}
</style>
